import React, { useState, useEffect } from 'react';
import './HighContrastTest.css';

const HighContrastTest = () => {
  const [highContrastStatus, setHighContrastStatus] = useState({});
  const [visibilityResults, setVisibilityResults] = useState([]);
  const [manualHighContrast, setManualHighContrast] = useState(false);

  useEffect(() => {
    // Get initial high contrast status
    updateHighContrastStatus();
    
    // Set up listeners for changes
    const updateStatus = () => updateHighContrastStatus();
    
    // Listen for media query changes
    if (window.matchMedia) {
      const highContrastQuery = window.matchMedia('(prefers-contrast: high)');
      const forcedColorsQuery = window.matchMedia('(forced-colors: active)');
      
      if (highContrastQuery.addEventListener) {
        highContrastQuery.addEventListener('change', updateStatus);
        forcedColorsQuery.addEventListener('change', updateStatus);
      }
      
      return () => {
        if (highContrastQuery.removeEventListener) {
          highContrastQuery.removeEventListener('change', updateStatus);
          forcedColorsQuery.removeEventListener('change', updateStatus);
        }
      };
    }
  }, []);

  const updateHighContrastStatus = () => {
    if (window.highContrastSupport) {
      setHighContrastStatus(window.highContrastSupport.getStatus());
    }
  };

  const toggleManualHighContrast = () => {
    if (window.highContrastSupport) {
      window.highContrastSupport.toggleHighContrast();
      setManualHighContrast(!manualHighContrast);
      updateHighContrastStatus();
    }
  };

  const testVisibility = () => {
    if (window.highContrastSupport) {
      const results = window.highContrastSupport.testHighContrastVisibility();
      setVisibilityResults(results);
    }
  };

  const StatusIndicator = ({ label, status }) => (
    <div className={`status-indicator ${status ? 'status-success' : 'status-error'}`}>
      <span className="status-label">{label}:</span>
      <span className="status-value">{status ? 'Supported/Active' : 'Not Supported/Inactive'}</span>
    </div>
  );

  return (
    <div className="high-contrast-test">
      <div className="test-container">
        <h1>High Contrast Mode Test</h1>
        <p>This page tests high contrast mode support and accessibility features.</p>

        {/* High Contrast Status */}
        <section className="test-section">
          <h2>High Contrast Status</h2>
          <div className="status-grid">
            <StatusIndicator 
              label="High Contrast Mode" 
              status={highContrastStatus.highContrast} 
            />
            <StatusIndicator 
              label="Forced Colors Mode" 
              status={highContrastStatus.forcedColors} 
            />
            <StatusIndicator 
              label="Windows High Contrast" 
              status={highContrastStatus.windowsHighContrast} 
            />
          </div>
        </section>

        {/* Browser Support */}
        <section className="test-section">
          <h2>Browser Feature Support</h2>
          <div className="status-grid">
            <StatusIndicator 
              label="CSS Custom Properties" 
              status={highContrastStatus.cssCustomProperties} 
            />
            <StatusIndicator 
              label="Backdrop Filter" 
              status={highContrastStatus.backdropFilter} 
            />
            <StatusIndicator 
              label="CSS Gradients" 
              status={highContrastStatus.gradients} 
            />
            <StatusIndicator 
              label="Forced Colors Support" 
              status={highContrastStatus.forcedColorsSupport} 
            />
          </div>
        </section>

        {/* Manual Controls */}
        <section className="test-section">
          <h2>Manual Testing Controls</h2>
          <div className="control-buttons">
            <button 
              className="btn-primary test-button"
              onClick={toggleManualHighContrast}
            >
              {manualHighContrast ? 'Disable' : 'Enable'} Manual High Contrast
            </button>
            <button 
              className="btn-secondary test-button"
              onClick={testVisibility}
            >
              Test Element Visibility
            </button>
            <button 
              className="btn-accent test-button"
              onClick={updateHighContrastStatus}
            >
              Refresh Status
            </button>
          </div>
        </section>

        {/* Test Elements */}
        <section className="test-section">
          <h2>Interactive Elements Test</h2>
          <p>These elements should remain visible and accessible in high contrast mode:</p>
          
          <div className="test-elements">
            {/* Buttons */}
            <div className="element-group">
              <h3>Buttons</h3>
              <button className="btn-primary">Primary Button</button>
              <button className="btn-secondary">Secondary Button</button>
              <button className="btn-accent">Accent Button</button>
              <button className="button-success">Success Button</button>
              <button className="button-warning">Warning Button</button>
              <button className="button-error">Error Button</button>
              <button className="button-info">Info Button</button>
            </div>

            {/* Form Elements */}
            <div className="element-group">
              <h3>Form Elements</h3>
              <input type="text" placeholder="Text input" className="test-input" />
              <input type="email" placeholder="Email input" className="test-input input-success" />
              <input type="password" placeholder="Password input" className="test-input input-warning" />
              <textarea placeholder="Textarea" className="test-textarea input-error"></textarea>
              <select className="test-select input-info">
                <option>Select option 1</option>
                <option>Select option 2</option>
                <option>Select option 3</option>
              </select>
            </div>

            {/* Links */}
            <div className="element-group">
              <h3>Links</h3>
              <a href="#test" className="test-link">Regular Link</a>
              <a href="#test" className="test-link nav-link">Navigation Link</a>
              <a href="#test" className="test-link text-accent">Accent Link</a>
            </div>

            {/* Semantic Components */}
            <div className="element-group">
              <h3>Semantic Components</h3>
              <div className="alert-success">Success Alert Message</div>
              <div className="alert-warning">Warning Alert Message</div>
              <div className="alert-error">Error Alert Message</div>
              <div className="alert-info">Info Alert Message</div>
              
              <div className="badge-group">
                <span className="badge-success">Success</span>
                <span className="badge-warning">Warning</span>
                <span className="badge-error">Error</span>
                <span className="badge-info">Info</span>
              </div>
            </div>

            {/* Cards */}
            <div className="element-group">
              <h3>Cards and Containers</h3>
              <div className="card test-card">
                <h4>Test Card</h4>
                <p>This is a test card with content that should be visible in high contrast mode.</p>
                <button className="btn-primary">Card Button</button>
              </div>
            </div>
          </div>
        </section>

        {/* Visibility Test Results */}
        {visibilityResults.length > 0 && (
          <section className="test-section">
            <h2>Visibility Test Results</h2>
            <div className="visibility-results">
              {visibilityResults.map((result, index) => (
                <div key={index} className={`visibility-result ${result.visible ? 'visible' : 'not-visible'}`}>
                  <strong>{result.element}</strong> ({result.type}): 
                  <span className={result.visible ? 'text-success' : 'text-error'}>
                    {result.visible ? ' Visible' : ' Not Visible'}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Instructions */}
        <section className="test-section">
          <h2>Testing Instructions</h2>
          <div className="instructions">
            <h3>System High Contrast Mode</h3>
            <ul>
              <li><strong>Windows:</strong> Settings → Ease of Access → High contrast</li>
              <li><strong>macOS:</strong> System Preferences → Accessibility → Display → Increase contrast</li>
              <li><strong>Linux:</strong> Varies by desktop environment</li>
            </ul>
            
            <h3>Browser Testing</h3>
            <ul>
              <li>Use browser developer tools to simulate high contrast mode</li>
              <li>Test with screen readers (NVDA, JAWS, VoiceOver)</li>
              <li>Verify keyboard navigation works properly</li>
              <li>Check that all interactive elements are focusable</li>
            </ul>
            
            <h3>Expected Behavior</h3>
            <ul>
              <li>All text should have sufficient contrast (4.5:1 minimum)</li>
              <li>Interactive elements should be clearly distinguishable</li>
              <li>Focus indicators should be highly visible</li>
              <li>Complex animations and effects should be simplified or removed</li>
              <li>Gradients should fallback to solid colors</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HighContrastTest;